import { Dispatch } from 'react';

import { DateGranularity, FormInputTypes } from '#/client/models';
import { FormInputVariants } from '#/service/constants';
import { falsyToNull } from '#/service/utils';
import { tb } from '#/store/translation/translation.store';

import {
  Action,
  FormAction,
  FormState,
  InputButtonRegular,
  InputButtonSubmit,
  InputField,
  InputFieldGroup,
  InputGroup,
  InputGroupType,
  InputPlanGroup,
  MainButtonGroupParams,
  SubmitButtonGroupParams,
  TValidation,
} from './Form.types';

const stringToBooleanExact = (
  value: string | undefined
): boolean | string | undefined => {
  if (value === 'true') {
    return true;
  }

  if (value === 'false') {
    return false;
  }

  return value;
};

export const getInitialState = (inputGroups: InputGroup[]) => {
  return inputGroups.reduce(
    (state: FormState, group: InputGroup) => {
      if (group.type === InputGroupType.Field) {
        group.inputs.forEach(({ key, defaultValue }) => {
          state.inputs[key] = {
            touched: false,
            // Liquid returns string, but we need boolean values here.
            value: stringToBooleanExact(defaultValue),
          };
        });
      }

      return state;
    },
    { inputs: {}, errorMessages: {} }
  );
};

export const reducer = (formState: FormState, action: Action): FormState => {
  if (!action.payload) {
    return formState;
  }

  const { type, payload } = action;
  const { errorMessages, inputState, key, initialState } = payload;

  switch (type) {
    case FormAction.Update:
      return {
        ...formState,
        inputs: {
          ...formState.inputs,
          [key]: {
            ...formState.inputs[key],
            ...inputState,
          },
        },
      };

    case FormAction.Error:
      return {
        ...formState,
        errorMessages: {
          ...formState.errorMessages,
          [key]: errorMessages,
        },
      };

    case FormAction.Reset:
      return initialState ?? formState;

    default:
      return formState;
  }
};

export const clearForm = (
  inputGroups: InputGroup[],
  dispatch: Dispatch<Action>
) => {
  const action = {
    type: FormAction.Reset,
    payload: {
      key: 'clearForm',
      initialState: getInitialState(inputGroups),
    },
  };

  dispatch(action);
};

export const getMainErrorMessage = (
  validationEntries: [string, string[]][],
  responseMessage?: string
) => {
  return (
    // check if only one field has validation error(s)
    (validationEntries.length === 1 &&
      // check if that field has a single validation error
      // (optional chaining is a safety measure in case backend doesn't return an array)
      validationEntries[0][1]?.length === 1 &&
      // if so, use it
      validationEntries[0][1][0]) ||
    // otherwise, if there's an error message in the response, use that
    responseMessage ||
    // fall back to the generic error message as a last resort
    tb('error.generic.message')
  );
};

export const createBasicFormConfig = (
  keyRoot: string,
  fields: InputField[],
  buttons: Omit<SubmitButtonGroupParams, 'hasSubmitButton'>
) => {
  return createFormConfig(
    keyRoot,
    { hasSubmitButton: true, ...buttons },
    {
      key: `${keyRoot}FieldGroup`,
      type: InputGroupType.Field,
      inputs: fields,
    }
  );
};

const createFormConfig = (
  keyRoot: string,
  buttons: SubmitButtonGroupParams | MainButtonGroupParams,
  primaryGroup: InputFieldGroup | InputPlanGroup
): InputGroup[] => {
  return [
    primaryGroup,
    {
      key: `${keyRoot}ButtonGroup`,
      type: InputGroupType.Button,
      inputs: [
        getPrimaryButton(keyRoot, buttons),
        getCancelButton(keyRoot, buttons),
      ].filter(Boolean),
    },
  ];
};

function getPrimaryButton(
  keyRoot: string,
  buttons: SubmitButtonGroupParams | MainButtonGroupParams
): InputButtonSubmit | InputButtonRegular | undefined {
  if (buttons.hasSubmitButton) {
    return {
      key: `${keyRoot}Submit`,
      type: FormInputTypes.INPUT,
      variant: FormInputVariants.SUBMIT,
      text: tb(buttons.submit.textKeyPath),
      onClick: buttons.submit.onClick,
      disclaimer: buttons.submit.disclaimer,
    };
  }

  const tempButton = buttons;

  if (tempButton.main) {
    return {
      key: `${keyRoot}Main`,
      isMainButton: true,
      type: FormInputTypes.INPUT,
      variant: FormInputVariants.BUTTON,
      text: tb(tempButton.main.textKeyPath),
      onClick: tempButton.main.onClick,
      disclaimer: tempButton.main.disclaimer,
    };
  }

  return undefined;
}

function getCancelButton(
  keyRoot: string,
  buttons: SubmitButtonGroupParams | MainButtonGroupParams
): InputButtonRegular | undefined {
  if (buttons.cancel) {
    return {
      key: `${keyRoot}Cancel`,
      type: FormInputTypes.INPUT,
      variant: FormInputVariants.BUTTON,
      text: tb(falsyToNull(buttons.cancel.textKeyPath) ?? 'button.cancel'),
      onClick: buttons.cancel.onClick,
    };
  }

  return undefined;
}

/** Validations functions */

//Zero out seconds and milliseconds from date
export const toDateAndTime = (d: Date) => {
  const date = new Date(d);
  date.setSeconds(0, 0);

  return date;
};

export const toDateOnly = (d: Date) =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate());

export const toTimeOnly = (d: Date) => {
  const date = new Date(0);
  date.setHours(d.getHours(), d.getMinutes(), 0, 0);

  return date;
};

const generateFallbackMessage = (min?: Date, max?: Date) => {
  if (min && !max) {
    return tb('error.timeStampInput.message.minOnly', {
      minDate: min,
    });
  }

  if (max && !min) {
    return tb('error.timeStampInput.message.maxOnly', {
      maxDate: max,
    });
  }

  return tb('error.timeStampInput.message.minMax', {
    minDate: min,
    maxDate: max,
  });
};

export const validateMinMax = (
  date: Date,
  message: string,
  min?: Date,
  max?: Date
) => {
  if ((min && date < min) || (max && date > max)) {
    return message.trim().length ? message : generateFallbackMessage(min, max);
  }
};

export const validateTimestamp = (
  timestamp: TValidation['timestamp'],
  inputValue: string,
  message: string,
  granularity: DateGranularity | undefined
) => {
  if (!timestamp) {
    return;
  }

  let date = new Date(inputValue);
  let min: Date | undefined;
  let max: Date | undefined;

  switch (granularity) {
    case DateGranularity.DateAndTime:
      date = toDateAndTime(date);
      min = timestamp.min ? toDateAndTime(new Date(timestamp.min)) : undefined;
      max = timestamp.max ? toDateAndTime(new Date(timestamp.max)) : undefined;

      return validateMinMax(date, message, min, max);

    case DateGranularity.TimeOnly:
      date = toTimeOnly(date);
      min = timestamp.min ? toTimeOnly(new Date(timestamp.min)) : undefined;
      max = timestamp.max ? toTimeOnly(new Date(timestamp.max)) : undefined;

      return validateMinMax(date, message, min, max);

    case DateGranularity.DateOnly:
      date = toDateOnly(date);
      min = timestamp.min ? toDateOnly(new Date(timestamp.min)) : undefined;
      max = timestamp.max ? toDateOnly(new Date(timestamp.max)) : undefined;

      return validateMinMax(date, message, min, max);
  }
};

export const validateRegexp = (
  regexp: string | undefined,
  inputValue: string,
  message: string
) => {
  if (regexp && !new RegExp(regexp).test(inputValue)) {
    return message;
  }
};

export const validateMatch = (
  match: string | undefined,
  inputValue: string,
  getFieldValue: (key?: string) => string | boolean | undefined,
  message: string
) => {
  if (match) {
    const otherInputValue = getFieldValue(match);

    if (inputValue !== otherInputValue) {
      return message;
    }
  }
};

export const getErrorMessagesIds = ({
  groupKey,
  dataKey,
  errorMessages,
}: {
  dataKey: string;
  errorMessages: string[];
  groupKey?: string;
}) => {
  return errorMessages?.length
    ? errorMessages
        .map((_, i) => `${groupKey}-${dataKey}-error-message-${i}`)
        .join(' ')
    : undefined;
};
